import { isActiveSeatHold, normalizeSeatLabel } from "./seatHoldStatus";

export const SEAT_CLAIM_TAKEN_MESSAGE = "That seat was just taken. Pick another.";
export const SEAT_CLAIM_AUTH_MESSAGE = "Sign in to select seats.";
export const SEAT_HOLD_EXPIRED_MESSAGE =
  "Your seat hold expired or is no longer available. Please re-select your seats.";

export const SELECTION_HOLD_MINUTES = 5;
export const CHECKOUT_HOLD_MINUTES = 15;

export function seatClaimKey(tierId, seatLabel) {
  return `${tierId}::${normalizeSeatLabel(seatLabel)}`;
}

export function buildTakenSeatsByTier(rows, { viewerUserId = null } = {}) {
  const next = {};
  (rows || []).forEach((row) => {
    const tierId = row?.ticket_tier_id;
    const seatLabel = normalizeSeatLabel(row?.seat_label);
    if (!tierId || !seatLabel) return;

    const source = String(row?.source || "").toLowerCase();
    if (source === "hold" && viewerUserId && row?.reserved_by === viewerUserId) {
      return;
    }

    if (!next[tierId]) next[tierId] = new Set();
    next[tierId].add(seatLabel);
  });

  return Object.fromEntries(
    Object.entries(next).map(([tierId, seatSet]) => [tierId, Array.from(seatSet)])
  );
}

export function buildTakenSeatsFromFallback({ tickets = [], holds = [], viewerUserId = null } = {}) {
  const rows = [
    ...(tickets || []).map((ticket) => ({
      ticket_tier_id: ticket.ticket_tier_id,
      seat_label: ticket.seat_label,
      source: "ticket",
      reserved_by: null,
    })),
    ...(holds || [])
      .filter((hold) => isActiveSeatHold(hold))
      .map((hold) => ({
        ticket_tier_id: hold.ticket_tier_id,
        seat_label: hold.seat_label,
        source: "hold",
        reserved_by: hold.reserved_by || null,
      })),
  ];
  return buildTakenSeatsByTier(rows, { viewerUserId });
}

export async function claimSeatHold(supabase, { eventId, ticketTierId, seatLabel, seatArea = "" }) {
  const { data, error } = await supabase.rpc("claim_seat_hold", {
    p_event_id: eventId,
    p_ticket_tier_id: ticketTierId,
    p_seat_label: normalizeSeatLabel(seatLabel),
    p_seat_area: String(seatArea || "").trim() || null,
  });

  if (error) {
    return { ok: false, reason: "error", error };
  }

  const payload = data && typeof data === "object" ? data : {};
  return {
    ok: Boolean(payload.ok),
    reason: String(payload.reason || (payload.ok ? "ok" : "taken")),
    holdId: payload.hold_id || null,
  };
}

export async function releaseSeatHold(supabase, { eventId, ticketTierId, seatLabel }) {
  const { data, error } = await supabase.rpc("release_seat_hold", {
    p_event_id: eventId,
    p_ticket_tier_id: ticketTierId,
    p_seat_label: normalizeSeatLabel(seatLabel),
  });

  if (error) {
    return { ok: false, reason: "error", error };
  }

  const payload = data && typeof data === "object" ? data : {};
  return {
    ok: payload.ok !== false,
    released: Number(payload.released || 0),
  };
}

export async function releaseMyEventSeatHolds(supabase, eventId) {
  const { data, error } = await supabase.rpc("release_my_event_seat_holds", {
    p_event_id: eventId,
  });

  if (error) {
    return { ok: false, reason: "error", error };
  }

  const payload = data && typeof data === "object" ? data : {};
  return {
    ok: payload.ok !== false,
    released: Number(payload.released || 0),
  };
}

export async function extendSeatHolds(
  supabase,
  { eventId, seats, minutes = CHECKOUT_HOLD_MINUTES }
) {
  const payloadSeats = (seats || [])
    .map((seat) => ({
      ticket_tier_id: seat.ticket_tier_id || seat.ticketTierId,
      seat_label: normalizeSeatLabel(seat.seat_label || seat.seatLabel),
    }))
    .filter((seat) => seat.ticket_tier_id && seat.seat_label);

  const { data, error } = await supabase.rpc("extend_seat_holds", {
    p_event_id: eventId,
    p_seats: payloadSeats,
    p_minutes: minutes,
  });

  if (error) {
    return { ok: false, reason: "error", error, missing: [] };
  }

  const payload = data && typeof data === "object" ? data : {};
  return {
    ok: Boolean(payload.ok),
    reason: String(payload.reason || (payload.ok ? "ok" : "missing_holds")),
    updated: Number(payload.updated || 0),
    missing: Array.isArray(payload.missing) ? payload.missing : [],
  };
}

export function seatsPayloadFromSelectedByTier(selectedSeatsByTier) {
  return Object.entries(selectedSeatsByTier || {}).flatMap(([tierId, seats]) =>
    (seats || []).map((seat) => ({
      ticket_tier_id: tierId,
      seat_label: normalizeSeatLabel(seat.label || seat.id),
    }))
  );
}

export function pruneSelectedSeatsAgainstTaken(selectedSeatsByTier, takenSeatsByTier) {
  let changed = false;
  const next = {};

  Object.entries(selectedSeatsByTier || {}).forEach(([tierId, seats]) => {
    const taken = new Set((takenSeatsByTier?.[tierId] || []).map(normalizeSeatLabel));
    const kept = (seats || []).filter((seat) => {
      const label = normalizeSeatLabel(seat.label || seat.id);
      if (taken.has(label)) {
        changed = true;
        return false;
      }
      return true;
    });
    if (kept.length) next[tierId] = kept;
    else if ((seats || []).length) changed = true;
  });

  return { next, changed };
}
